import { afterTick, box, mergeProps, onDestroyEffect, useRefById } from "svelte-toolbelt";
import { Context, watch } from "runed";
import { FIRST_LAST_KEYS, LAST_KEYS, SELECTION_KEYS, SUB_OPEN_KEYS, getCheckedState, isMouseEvent, } from "./utils.js";
import { focusFirst } from "../../internal/focus.js";
import { CustomEventDispatcher } from "../../internal/events.js";
import { useDOMTypeahead } from "../../internal/use-dom-typeahead.svelte.js";
import { isElement, isElementOrSVGElement, isHTMLElement } from "../../internal/is.js";
import { useRovingFocus } from "../../internal/use-roving-focus.svelte.js";
import { kbd } from "../../internal/kbd.js";
import { getAriaChecked, getAriaDisabled, getAriaExpanded, getAriaOrientation, getDataDisabled, getDataOpenClosed, } from "../../internal/attrs.js";
import { IsUsingKeyboard } from "../../index.js";
import { useGraceArea } from "../../internal/use-grace-area.svelte.js";
import { getTabbableFrom } from "../../internal/tabbable.js";
import { FocusScopeContext } from "../utilities/focus-scope/use-focus-scope.svelte.js";
import { isTabbable } from "tabbable";
export const CONTEXT_MENU_TRIGGER_ATTR = "data-context-menu-trigger";
const MenuRootContext = new Context("Menu.Root");
const MenuMenuContext = new Context("Menu.Root | Menu.Sub");
const MenuContentContext = new Context("Menu.Content");
const MenuGroupContext = new Context("Menu.Group | Menu.RadioGroup");
const MenuRadioGroupContext = new Context("Menu.RadioGroup");
export const MenuOpenEvent = new CustomEventDispatcher("bitsmenuopen", {
    bubbles: false,
    cancelable: true,
});
class MenuRootState {
    opts;
    isUsingKeyboard = new IsUsingKeyboard();
    ignoreCloseAutoFocus = $state(false);
    isPointerInTransit = $state(false);
    constructor(opts) {
        this.opts = opts;
    }
    getAttr(name) {
        return `data-${this.opts.variant.current}-${name}`;
    }
}
class MenuMenuState {
    opts;
    root;
    parentMenu;
    contentId = box.with(() => "");
    contentNode = $state(null);
    triggerNode = $state(null);
    constructor(opts, root, parentMenu) {
        this.opts = opts;
        this.root = root;
        this.parentMenu = parentMenu;
        if (parentMenu) {
            watch(() => parentMenu.opts.open.current, () => {
                if (parentMenu.opts.open.current)
                    return;
                this.opts.open.current = false;
            });
        }
    }
    toggleOpen() {
        this.opts.open.current = !this.opts.open.current;
    }
    onOpen() {
        this.opts.open.current = true;
    }
    onClose() {
        this.opts.open.current = false;
    }
}
class MenuContentState {
    opts;
    parentMenu;
    search = $state("");
    #timer = 0;
    #handleTypeaheadSearch;
    rovingFocusGroup;
    mounted = $state(false);
    #isSub;
    constructor(opts, parentMenu) {
        this.opts = opts;
        this.parentMenu = parentMenu;
        parentMenu.contentId = opts.id;
        this.#isSub = opts.isSub ?? false;
        this.onkeydown = this.onkeydown.bind(this);
        this.onblur = this.onblur.bind(this);
        this.onfocus = this.onfocus.bind(this);
        this.handleInteractOutside = this.handleInteractOutside.bind(this);
        useRefById({
            ...opts,
            deps: () => this.parentMenu.opts.open.current,
            onRefChange: (node) => {
                if (this.parentMenu.contentNode !== node) {
                    this.parentMenu.contentNode = node;
                }
            },
        });
        useGraceArea({
            contentNode: () => this.parentMenu.contentNode,
            triggerNode: () => this.parentMenu.triggerNode,
            enabled: () => this.parentMenu.opts.open.current &&
                Boolean(this.parentMenu.triggerNode?.hasAttribute(this.parentMenu.root.getAttr("sub-trigger"))),
            onPointerExit: () => {
                this.parentMenu.opts.open.current = false;
            },
            setIsPointerInTransit: (value) => {
                this.parentMenu.root.isPointerInTransit = value;
            },
        });
        this.#handleTypeaheadSearch = useDOMTypeahead().handleTypeaheadSearch;
        this.rovingFocusGroup = useRovingFocus({
            rootNodeId: this.parentMenu.contentId,
            candidateAttr: this.parentMenu.root.getAttr("item"),
            loop: this.opts.loop,
            orientation: box.with(() => "vertical"),
        });
        watch(() => this.parentMenu.contentNode, (contentNode) => {
            if (!contentNode)
                return;
            const handler = () => {
                afterTick(() => {
                    if (!this.parentMenu.root.isUsingKeyboard.current)
                        return;
                    this.rovingFocusGroup.focusFirstCandidate();
                });
            };
            return MenuOpenEvent.listen(contentNode, handler);
        });
        $effect(() => {
            if (!this.parentMenu.opts.open.current) {
                window.clearTimeout(this.#timer);
            }
        });
    }
    #getCandidateNodes() {
        const node = this.parentMenu.contentNode;
        if (!node)
            return [];
        const candidates = Array.from(node.querySelectorAll(`[${this.parentMenu.root.getAttr("item")}]:not([data-disabled])`));
        return candidates;
    }
    #isPointerMovingToSubmenu() {
        return this.parentMenu.root.isPointerInTransit;
    }
    onCloseAutoFocus = (e) => {
        this.opts.onCloseAutoFocus.current(e);
        if (e.defaultPrevented || this.#isSub)
            return;
        if (this.parentMenu.triggerNode && isTabbable(this.parentMenu.triggerNode)) {
            this.parentMenu.triggerNode.focus();
        }
    };
    handleTabKeyDown(e) {
        /**
         * We locate the root `menu`'s trigger by going up the tree until
         * we find a menu that has no parent. This will allow us to focus the next
         * tabbable element before/after the root trigger.
         */
        let rootMenu = this.parentMenu;
        while (rootMenu.parentMenu !== null) {
            rootMenu = rootMenu.parentMenu;
        }
        // if for some unforeseen reason the root menu has no trigger, we bail
        if (!rootMenu.triggerNode)
            return;
        // cancel default tab behavior
        e.preventDefault();
        // find the next/previous tabbable
        const nodeToFocus = getTabbableFrom(rootMenu.triggerNode, e.shiftKey ? "prev" : "next");
        if (nodeToFocus) {
            /**
             * We set a flag to ignore the `onCloseAutoFocus` event handler
             * as well as the fallbacks inside the focus scope to prevent
             * race conditions causing focus to fall back to the body even
             * though we're trying to focus the next tabbable element.
             */
            this.parentMenu.root.ignoreCloseAutoFocus = true;
            rootMenu.onClose();
            nodeToFocus.focus();
            afterTick(() => {
                this.parentMenu.root.ignoreCloseAutoFocus = false;
            });
        }
        else {
            document.body.focus();
        }
    }
    onkeydown(e) {
        if (e.defaultPrevented)
            return;
        if (e.key === kbd.TAB) {
            this.handleTabKeyDown(e);
            return;
        }
        const target = e.target;
        const currentTarget = e.currentTarget;
        if (!isHTMLElement(target) || !isHTMLElement(currentTarget))
            return;
        const isKeydownInside = target.closest(`[${this.parentMenu.root.getAttr("content")}]`)?.id ===
            this.parentMenu.contentId.current;
        const isModifierKey = e.ctrlKey || e.altKey || e.metaKey;
        const isCharacterKey = e.key.length === 1;
        const kbdFocusedEl = this.rovingFocusGroup.handleKeydown(target, e);
        if (kbdFocusedEl)
            return;
        // prevent space from being considered with typeahead
        if (e.code === "Space")
            return;
        const candidateNodes = this.#getCandidateNodes();
        if (isKeydownInside) {
            if (!isModifierKey && isCharacterKey) {
                this.#handleTypeaheadSearch(e.key, candidateNodes);
            }
        }
        // focus first/last based on key pressed
        if (e.target?.id !== this.parentMenu.contentId.current)
            return;
        if (!FIRST_LAST_KEYS.includes(e.key))
            return;
        e.preventDefault();
        if (LAST_KEYS.includes(e.key)) {
            candidateNodes.reverse();
        }
        focusFirst(candidateNodes);
    }
    onblur(e) {
        if (!isElement(e.currentTarget))
            return;
        if (!isElement(e.target))
            return;
        // clear search buffer when leaving the menu
        if (!e.currentTarget.contains?.(e.target)) {
            window.clearTimeout(this.#timer);
            this.search = "";
        }
    }
    onfocus(_) {
        if (!this.parentMenu.root.isUsingKeyboard.current)
            return;
        afterTick(() => this.rovingFocusGroup.focusFirstCandidate());
    }
    onItemEnter() {
        return this.#isPointerMovingToSubmenu();
    }
    onItemLeave(e) {
        if (e.currentTarget.hasAttribute(this.parentMenu.root.getAttr("sub-trigger")))
            return;
        if (this.#isPointerMovingToSubmenu() || this.parentMenu.root.isUsingKeyboard.current)
            return;
        const contentNode = this.parentMenu.contentNode;
        contentNode?.focus();
        this.rovingFocusGroup.setCurrentTabStopId("");
    }
    onTriggerLeave() {
        if (this.#isPointerMovingToSubmenu())
            return true;
        return false;
    }
    onOpenAutoFocus = (e) => {
        if (e.defaultPrevented)
            return;
        e.preventDefault();
        const contentNode = this.parentMenu.contentNode;
        contentNode?.focus();
    };
    handleInteractOutside(e) {
        if (!isElementOrSVGElement(e.target))
            return;
        const triggerId = this.parentMenu.triggerNode?.id;
        if (e.target.id === triggerId) {
            e.preventDefault();
            return;
        }
        if (e.target.closest(`#${triggerId}`)) {
            e.preventDefault();
        }
    }
    snippetProps = $derived.by(() => ({ open: this.parentMenu.opts.open.current }));
    props = $derived.by(() => ({
        id: this.opts.id.current,
        role: "menu",
        "aria-orientation": getAriaOrientation("vertical"),
        [this.parentMenu.root.getAttr("content")]: "",
        "data-state": getDataOpenClosed(this.parentMenu.opts.open.current),
        onkeydown: this.onkeydown,
        onblur: this.onblur,
        onfocus: this.onfocus,
        dir: this.parentMenu.root.opts.dir.current,
        style: {
            pointerEvents: "auto",
        },
    }));
    popperProps = {
        onCloseAutoFocus: (e) => this.onCloseAutoFocus(e),
    };
}
class MenuItemSharedState {
    opts;
    content;
    #isFocused = $state(false);
    constructor(opts, content) {
        this.opts = opts;
        this.content = content;
        this.onpointermove = this.onpointermove.bind(this);
        this.onpointerleave = this.onpointerleave.bind(this);
        this.onfocus = this.onfocus.bind(this);
        this.onblur = this.onblur.bind(this);
        useRefById({
            ...opts,
            deps: () => this.content.mounted,
        });
    }
    onpointermove(e) {
        if (e.defaultPrevented)
            return;
        if (!isMouseEvent(e))
            return;
        if (this.opts.disabled.current) {
            this.content.onItemLeave(e);
        }
        else {
            const defaultPrevented = this.content.onItemEnter();
            if (defaultPrevented)
                return;
            const item = e.currentTarget;
            if (!isHTMLElement(item))
                return;
            item.focus();
        }
    }
    onpointerleave(e) {
        if (e.defaultPrevented)
            return;
        if (!isMouseEvent(e))
            return;
        this.content.onItemLeave(e);
    }
    onfocus(e) {
        afterTick(() => {
            if (e.defaultPrevented || this.opts.disabled.current)
                return;
            this.#isFocused = true;
        });
    }
    onblur(e) {
        afterTick(() => {
            if (e.defaultPrevented)
                return;
            this.#isFocused = false;
        });
    }
    props = $derived.by(() => ({
        id: this.opts.id.current,
        tabindex: -1,
        role: "menuitem",
        "aria-disabled": getAriaDisabled(this.opts.disabled.current),
        "data-disabled": getDataDisabled(this.opts.disabled.current),
        "data-highlighted": this.#isFocused ? "" : undefined,
        [this.content.parentMenu.root.getAttr("item")]: "",
        //
        onpointermove: this.onpointermove,
        onpointerleave: this.onpointerleave,
        onfocus: this.onfocus,
        onblur: this.onblur,
    }));
}
class MenuItemState {
    opts;
    item;
    #isPointerDown = false;
    root;
    constructor(opts, item) {
        this.opts = opts;
        this.item = item;
        this.root = item.content.parentMenu.root;
        this.onkeydown = this.onkeydown.bind(this);
        this.onclick = this.onclick.bind(this);
        this.onpointerdown = this.onpointerdown.bind(this);
        this.onpointerup = this.onpointerup.bind(this);
    }
    #handleSelect() {
        if (this.item.opts.disabled.current)
            return;
        const selectEvent = new CustomEvent("menuitemselect", { bubbles: true, cancelable: true });
        this.opts.onSelect.current(selectEvent);
        afterTick(() => {
            if (selectEvent.defaultPrevented) {
                this.item.content.parentMenu.root.isUsingKeyboard.current = false;
                return;
            }
            if (this.opts.closeOnSelect.current) {
                this.item.content.parentMenu.root.opts.onClose();
            }
        });
    }
    onkeydown(e) {
        const isTypingAhead = this.item.content.search !== "";
        if (this.item.opts.disabled.current || (isTypingAhead && e.key === kbd.SPACE))
            return;
        if (SELECTION_KEYS.includes(e.key)) {
            if (!isHTMLElement(e.currentTarget))
                return;
            e.currentTarget.click();
            /**
             * We prevent default browser behavior for selection keys as they should trigger
             * a selection only:
             * - prevents space from scrolling the page.
             * - if keydown causes focus to move, prevents keydown from firing on the new target.
             */
            e.preventDefault();
        }
    }
    onclick(_) {
        if (this.item.opts.disabled.current)
            return;
        this.#handleSelect();
    }
    onpointerup(e) {
        if (e.defaultPrevented)
            return;
        if (!this.#isPointerDown) {
            if (!isHTMLElement(e.currentTarget))
                return;
            e.currentTarget?.click();
        }
    }
    onpointerdown(_) {
        this.#isPointerDown = true;
    }
    props = $derived.by(() => mergeProps(this.item.props, {
        onclick: this.onclick,
        onpointerdown: this.onpointerdown,
        onpointerup: this.onpointerup,
        onkeydown: this.onkeydown,
    }));
}
class MenuSubTriggerState {
    item;
    content;
    submenu;
    #openTimer = null;
    constructor(item, content, submenu) {
        this.item = item;
        this.content = content;
        this.submenu = submenu;
        this.onpointerleave = this.onpointerleave.bind(this);
        this.onpointermove = this.onpointermove.bind(this);
        this.onkeydown = this.onkeydown.bind(this);
        this.onclick = this.onclick.bind(this);
        onDestroyEffect(() => {
            this.#clearOpenTimer();
        });
        useRefById({
            ...item.opts,
            onRefChange: (node) => {
                this.submenu.triggerNode = node;
            },
        });
    }
    #clearOpenTimer() {
        if (this.#openTimer === null)
            return;
        window.clearTimeout(this.#openTimer);
        this.#openTimer = null;
    }
    onpointermove(e) {
        if (!isMouseEvent(e))
            return;
        if (!this.item.opts.disabled.current &&
            !this.submenu.opts.open.current &&
            !this.#openTimer) {
            this.#openTimer = window.setTimeout(() => {
                this.submenu.onOpen();
                this.#clearOpenTimer();
            }, 100);
        }
    }
    onpointerleave(e) {
        if (!isMouseEvent(e))
            return;
        this.#clearOpenTimer();
    }
    onkeydown(e) {
        const isTypingAhead = this.content.search !== "";
        if (this.item.opts.disabled.current || (isTypingAhead && e.key === kbd.SPACE))
            return;
        if (SUB_OPEN_KEYS[this.submenu.root.opts.dir.current].includes(e.key)) {
            this.submenu.onOpen();
            afterTick(() => {
                const contentNode = this.submenu.contentNode;
                if (!contentNode)
                    return;
                MenuOpenEvent.dispatch(contentNode);
            });
            e.preventDefault();
        }
    }
    onclick(e) {
        if (this.item.opts.disabled.current)
            return;
        /**
         * We manually focus because iOS Safari doesn't always focus on click (e.g. buttons)
         * and we rely heavily on `onFocusOutside` for submenus to close when switching
         * between separate submenus.
         */
        if (!isHTMLElement(e.currentTarget))
            return;
        e.currentTarget.focus();
        if (!this.submenu.opts.open.current) {
            this.submenu.onOpen();
        }
    }
    props = $derived.by(() => mergeProps({
        "aria-haspopup": "menu",
        "aria-expanded": getAriaExpanded(this.submenu.opts.open.current),
        "data-state": getDataOpenClosed(this.submenu.opts.open.current),
        "aria-controls": this.submenu.opts.open.current
            ? this.submenu.contentId.current
            : undefined,
        [this.submenu.root.getAttr("sub-trigger")]: "",
        onclick: this.onclick,
        onpointermove: this.onpointermove,
        onpointerleave: this.onpointerleave,
        onkeydown: this.onkeydown,
    }, this.item.props));
}
class MenuCheckboxItemState {
    opts;
    item;
    constructor(opts, item) {
        this.opts = opts;
        this.item = item;
    }
    toggleChecked() {
        if (this.opts.indeterminate.current) {
            this.opts.indeterminate.current = false;
            this.opts.checked.current = true;
        }
        else {
            this.opts.checked.current = !this.opts.checked.current;
        }
    }
    snippetProps = $derived.by(() => ({
        checked: this.opts.checked.current,
        indeterminate: this.opts.indeterminate.current,
    }));
    props = $derived.by(() => ({
        ...this.item.props,
        role: "menuitemcheckbox",
        "aria-checked": getAriaChecked(this.opts.checked.current, this.opts.indeterminate.current),
        "data-state": getCheckedState(this.opts.checked.current),
        [this.item.root.getAttr("checkbox-item")]: "",
    }));
}
class MenuGroupState {
    opts;
    root;
    groupHeadingId = $state(undefined);
    constructor(opts, root) {
        this.opts = opts;
        this.root = root;
        useRefById(this.opts);
    }
    props = $derived.by(() => ({
        id: this.opts.id.current,
        role: "group",
        "aria-labelledby": this.groupHeadingId,
        [this.root.getAttr("group")]: "",
    }));
}
class MenuGroupHeadingState {
    opts;
    group;
    constructor(opts, group) {
        this.opts = opts;
        this.group = group;
        useRefById({
            ...opts,
            onRefChange: (node) => {
                this.group.groupHeadingId = node?.id;
            },
        });
    }
    props = $derived.by(() => ({
        id: this.opts.id.current,
        role: "group",
        [this.group.root.getAttr("group-heading")]: "",
    }));
}
class MenuSeparatorState {
    opts;
    root;
    constructor(opts, root) {
        this.opts = opts;
        this.root = root;
        useRefById(opts);
    }
    props = $derived.by(() => ({
        id: this.opts.id.current,
        role: "group",
        [this.root.getAttr("separator")]: "",
    }));
}
class MenuArrowState {
    root;
    constructor(root) {
        this.root = root;
    }
    props = $derived.by(() => ({
        [this.root.getAttr("arrow")]: "",
    }));
}
class MenuRadioGroupState {
    opts;
    content;
    groupHeadingId = $state(null);
    root;
    constructor(opts, content) {
        this.opts = opts;
        this.content = content;
        this.content = content;
        this.root = content.parentMenu.root;
        useRefById(opts);
    }
    setValue(v) {
        this.opts.value.current = v;
    }
    props = $derived.by(() => ({
        id: this.opts.id.current,
        [this.root.getAttr("radio-group")]: "",
        role: "group",
        "aria-labelledby": this.groupHeadingId,
    }));
}
class MenuRadioItemState {
    opts;
    item;
    group;
    isChecked = $derived.by(() => this.group.opts.value.current === this.opts.value.current);
    constructor(opts, item, group) {
        this.opts = opts;
        this.item = item;
        this.group = group;
        useRefById(opts);
    }
    selectValue() {
        this.group.setValue(this.opts.value.current);
    }
    props = $derived.by(() => ({
        [this.group.root.getAttr("radio-item")]: "",
        ...this.item.props,
        role: "menuitemradio",
        "aria-checked": getAriaChecked(this.isChecked, false),
        "data-state": getCheckedState(this.isChecked),
    }));
}
class DropdownMenuTriggerState {
    opts;
    parentMenu;
    constructor(opts, parentMenu) {
        this.opts = opts;
        this.parentMenu = parentMenu;
        this.onpointerdown = this.onpointerdown.bind(this);
        this.onpointerup = this.onpointerup.bind(this);
        this.onkeydown = this.onkeydown.bind(this);
        useRefById({
            ...opts,
            onRefChange: (ref) => {
                this.parentMenu.triggerNode = ref;
            },
        });
    }
    onpointerdown(e) {
        if (this.opts.disabled.current)
            return;
        if (e.pointerType === "touch")
            return e.preventDefault();
        if (e.button === 0 && e.ctrlKey === false) {
            this.parentMenu.toggleOpen();
            // prevent trigger focusing when opening to allow
            // the content to be given focus without competition
            if (!this.parentMenu.opts.open.current)
                e.preventDefault();
        }
    }
    onpointerup(e) {
        if (this.opts.disabled.current)
            return;
        if (e.pointerType === "touch") {
            e.preventDefault();
            this.parentMenu.toggleOpen();
        }
    }
    onkeydown(e) {
        if (this.opts.disabled.current)
            return;
        if (e.key === kbd.SPACE || e.key === kbd.ENTER) {
            this.parentMenu.toggleOpen();
            e.preventDefault();
            return;
        }
        if (e.key === kbd.ARROW_DOWN) {
            this.parentMenu.onOpen();
            e.preventDefault();
        }
    }
    #ariaControls = $derived.by(() => {
        if (this.parentMenu.opts.open.current && this.parentMenu.contentId.current)
            return this.parentMenu.contentId.current;
        return undefined;
    });
    props = $derived.by(() => ({
        id: this.opts.id.current,
        disabled: this.opts.disabled.current,
        "aria-haspopup": "menu",
        "aria-expanded": getAriaExpanded(this.parentMenu.opts.open.current),
        "aria-controls": this.#ariaControls,
        "data-disabled": getDataDisabled(this.opts.disabled.current),
        "data-state": getDataOpenClosed(this.parentMenu.opts.open.current),
        [this.parentMenu.root.getAttr("trigger")]: "",
        //
        onpointerdown: this.onpointerdown,
        onpointerup: this.onpointerup,
        onkeydown: this.onkeydown,
    }));
}
class ContextMenuTriggerState {
    opts;
    parentMenu;
    #point = $state({ x: 0, y: 0 });
    virtualElement = box({
        getBoundingClientRect: () => DOMRect.fromRect({ width: 0, height: 0, ...this.#point }),
    });
    #longPressTimer = null;
    constructor(opts, parentMenu) {
        this.opts = opts;
        this.parentMenu = parentMenu;
        this.oncontextmenu = this.oncontextmenu.bind(this);
        this.onpointerdown = this.onpointerdown.bind(this);
        this.onpointermove = this.onpointermove.bind(this);
        this.onpointercancel = this.onpointercancel.bind(this);
        this.onpointerup = this.onpointerup.bind(this);
        useRefById({
            ...opts,
            onRefChange: (node) => {
                this.parentMenu.triggerNode = node;
            },
            deps: () => this.parentMenu.opts.open.current,
        });
        watch(() => this.#point, (point) => {
            this.virtualElement.current = {
                getBoundingClientRect: () => DOMRect.fromRect({ width: 0, height: 0, ...point }),
            };
        });
        $effect(() => {
            if (this.opts.disabled.current) {
                this.#clearLongPressTimer();
            }
        });
        onDestroyEffect(() => this.#clearLongPressTimer());
    }
    #clearLongPressTimer() {
        if (this.#longPressTimer === null)
            return;
        window.clearTimeout(this.#longPressTimer);
    }
    #handleOpen(e) {
        this.#point = { x: e.clientX, y: e.clientY };
        this.parentMenu.onOpen();
    }
    oncontextmenu(e) {
        if (this.opts.disabled.current)
            return;
        this.#clearLongPressTimer();
        this.#handleOpen(e);
        e.preventDefault();
        this.parentMenu.contentNode?.focus();
    }
    onpointerdown(e) {
        if (this.opts.disabled.current || isMouseEvent(e))
            return;
        this.#clearLongPressTimer();
        this.#longPressTimer = window.setTimeout(() => this.#handleOpen(e), 700);
    }
    onpointermove(e) {
        if (this.opts.disabled.current || isMouseEvent(e))
            return;
        this.#clearLongPressTimer();
    }
    onpointercancel(e) {
        if (this.opts.disabled.current || isMouseEvent(e))
            return;
        this.#clearLongPressTimer();
    }
    onpointerup(e) {
        if (this.opts.disabled.current || isMouseEvent(e))
            return;
        this.#clearLongPressTimer();
    }
    props = $derived.by(() => ({
        id: this.opts.id.current,
        disabled: this.opts.disabled.current,
        "data-disabled": getDataDisabled(this.opts.disabled.current),
        "data-state": getDataOpenClosed(this.parentMenu.opts.open.current),
        [CONTEXT_MENU_TRIGGER_ATTR]: "",
        tabindex: -1,
        //
        onpointerdown: this.onpointerdown,
        onpointermove: this.onpointermove,
        onpointercancel: this.onpointercancel,
        onpointerup: this.onpointerup,
        oncontextmenu: this.oncontextmenu,
    }));
}
export function useMenuRoot(props) {
    const root = new MenuRootState(props);
    FocusScopeContext.set({
        get ignoreCloseAutoFocus() {
            return root.ignoreCloseAutoFocus;
        },
    });
    return MenuRootContext.set(root);
}
export function useMenuMenu(root, props) {
    return MenuMenuContext.set(new MenuMenuState(props, root, null));
}
export function useMenuSubmenu(props) {
    const menu = MenuMenuContext.get();
    return MenuMenuContext.set(new MenuMenuState(props, menu.root, menu));
}
export function useMenuSubTrigger(props) {
    const content = MenuContentContext.get();
    const item = new MenuItemSharedState(props, content);
    const submenu = MenuMenuContext.get();
    return new MenuSubTriggerState(item, content, submenu);
}
export function useMenuDropdownTrigger(props) {
    return new DropdownMenuTriggerState(props, MenuMenuContext.get());
}
export function useMenuContextTrigger(props) {
    return new ContextMenuTriggerState(props, MenuMenuContext.get());
}
export function useMenuContent(props) {
    return MenuContentContext.set(new MenuContentState(props, MenuMenuContext.get()));
}
export function useMenuItem(props) {
    const item = new MenuItemSharedState(props, MenuContentContext.get());
    return new MenuItemState(props, item);
}
export function useMenuCheckboxItem(props) {
    const item = new MenuItemState(props, new MenuItemSharedState(props, MenuContentContext.get()));
    return new MenuCheckboxItemState(props, item);
}
export function useMenuRadioGroup(props) {
    return MenuGroupContext.set(MenuRadioGroupContext.set(new MenuRadioGroupState(props, MenuContentContext.get())));
}
export function useMenuRadioItem(props) {
    const radioGroup = MenuRadioGroupContext.get();
    const sharedItem = new MenuItemSharedState(props, radioGroup.content);
    const item = new MenuItemState(props, sharedItem);
    return new MenuRadioItemState(props, item, radioGroup);
}
export function useMenuGroup(props) {
    return MenuGroupContext.set(new MenuGroupState(props, MenuRootContext.get()));
}
export function useMenuGroupHeading(props) {
    return new MenuGroupHeadingState(props, MenuGroupContext.get());
}
export function useMenuSeparator(props) {
    return new MenuSeparatorState(props, MenuRootContext.get());
}
export function useMenuArrow() {
    return new MenuArrowState(MenuRootContext.get());
}
